import { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus, Star, MapPin, Edit2, Settings, X, Clock, Trash2, AlertCircle, Zap, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import TimePicker12, {
  DAY_RANGE_OPTIONS,
  generateSlotsFromRange,
  time12hToMinutes,
  parseSlotRangeToMinutes,
  parseTimeTo12h,
  format12hTime,
  sortAndDeduplicateSlots,
  defaultSlotsList,
  getTurfPriceRange
} from './TimePicker12';

import { API_URL as API } from '../../config/api';

const GREEN = '#084734';
const LIME  = '#CEF17B';

const STATE_CITY_PINCODES = {
  'Andhra Pradesh': {
    'Visakhapatnam': '530001', 'Vijayawada': '520001', 'Guntur': '522001', 'Nellore': '524001', 
    'Kurnool': '518001', 'Tirupati': '517501', 'Kakinada': '533001', 'Rajahmundry': '533101', 
    'Kadapa': '516001', 'Eluru': '534001', 'Ongole': '523001', 'Vizianagaram': '535001', 
    'Anantapur': '515001', 'Chittoor': '517001', 'Srikakulam': '532001', 'Bhimavaram': '534201', 
    'Machilipatnam': '521001', 'Narasaraopet': '522601', 'Proddatur': '516360', 'Hindupur': '515201', 
    'Adoni': '518301', 'Tenali': '522201', 'Chirala': '523155', 'Nandyal': '518501', 
    'Tadipatri': '515411', 'Madanapalle': '517325', 'Gudivada': '521301', 'Mangalagiri': '522503', 
    'Tadepalligudem': '534101', 'Puttaparthi': '515134'
  },
  'Arunachal Pradesh': {
    'Itanagar': '791111', 'Naharlagun': '791110', 'Pasighat': '791102', 'Tawang': '790104', 
    'Ziro': '791120', 'Bomdila': '790001', 'Tezu': '792001', 'Roing': '792110', 
    'Changlang': '792120', 'Namsai': '792103', 'Aalo': '791001', 'Seppa': '790102'
  },
  'Assam': {
    'Guwahati': '781001', 'Silchar': '788001', 'Dibrugarh': '786001', 'Jorhat': '785001', 
    'Nagaon': '782001', 'Tinsukia': '786125', 'Tezpur': '784001', 'Bongaigaon': '783380', 
    'Dhubri': '783301', 'Diphu': '782460', 'Karimganj': '788710', 'Goalpara': '783101', 
    'Hailakandi': '788151', 'Sivasagar': '785640', 'Golaghat': '785621', 'Lakhimpur': '787001', 
    'Barpeta': '781301', 'Nalbari': '781335', 'Kokrajhar': '783370', 'Hojai': '782435', 
    'Dhemaji': '787057', 'Morigaon': '782105'
  },
  'Bihar': {
    'Patna': '800001', 'Gaya': '823001', 'Bhagalpur': '812001', 'Muzaffarpur': '842001', 
    'Purnia': '854301', 'Darbhanga': '846004', 'Arrah': '802301', 'Bihar Sharif': '803101', 
    'Begusarai': '851101', 'Katihar': '854105', 'Munger': '811201', 'Chhapra': '841301', 
    'Dehri': '821307', 'Bettiah': '845438', 'Motihari': '845401', 'Hajipur': '844101', 
    'Jehanabad': '804408', 'Nawada': '805110', 'Siwan': '841226', 'Sasaram': '821115', 
    'Aurangabad': '824101', 'Buxar': '802101', 'Kishanganj': '855107', 'Sitamarhi': '843302', 
    'Supaul': '852131', 'Madhubani': '847211', 'Samastipur': '848101'
  },
  'Chhattisgarh': {
    'Raipur': '492001', 'Bhilai': '490001', 'Bilaspur': '495001', 'Korba': '495677', 
    'Raigarh': '496001', 'Durg': '491001', 'Rajnandgaon': '491441', 'Jagdalpur': '494001', 
    'Ambikapur': '497001', 'Dhamtari': '493773', 'Chirmiri': '497449', 'Kanker': '494334', 
    'Mahasamund': '493445', 'Kawardha': '491995', 'Balod': '491226', 'Mungeli': '495334', 
    'Kondagaon': '494226', 'Narayanpur': '494661'
  },
  'Goa': {
    'Panaji': '403001', 'Margao': '403601', 'Vasco da Gama': '403802', 'Mapusa': '403507', 
    'Ponda': '403401', 'Bicholim': '403504', 'Curchorem': '403706', 'Sanquelim': '403505', 
    'Cuncolim': '403703', 'Valpoi': '403506'
  },
  'Gujarat': {
    'Ahmedabad': '380001', 'Surat': '395003', 'Vadodara': '390001', 'Rajkot': '360001', 
    'Bhavnagar': '364001', 'Jamnagar': '361001', 'Gandhinagar': '382010', 'Junagadh': '362001', 
    'Anand': '388001', 'Nadiad': '387001', 'Morbi': '363641', 'Surendranagar': '363001', 
    'Mehsana': '384001', 'Bharuch': '392001', 'Navsari': '396445', 'Valsad': '396001', 
    'Amreli': '365601', 'Porbandar': '360575', 'Palanpur': '385001', 'Godhra': '389001', 
    'Botad': '364710', 'Patan': '384265', 'Dahod': '389151', 'Veraval': '362265', 
    'Gandhidham': '370201', 'Deesa': '385535', 'Himatnagar': '383001', 'Modasa': '383315', 
    'Kalol': '382721'
  },
  'Haryana': {
    'Gurugram': '122001', 'Faridabad': '121001', 'Panipat': '132103', 'Ambala': '133001', 
    'Hisar': '125001', 'Rohtak': '124001', 'Karnal': '132001', 'Sonipat': '131001', 
    'Yamunanagar': '135001', 'Panchkula': '134109', 'Bhiwani': '127021', 'Sirsa': '125055', 
    'Bahadurgarh': '124507', 'Jind': '126102', 'Thanesar': '136118', 'Kaithal': '136027', 
    'Palwal': '121102', 'Rewari': '123401', 'Narnaul': '123001', 'Fatehabad': '125050', 
    'Mahendragarh': '123029'
  },
  'Himachal Pradesh': {
    'Shimla': '171001', 'Manali': '175131', 'Dharamshala': '176215', 'Solan': '173212', 
    'Mandi': '175001', 'Palampur': '176061', 'Baddi': '173205', 'Nahan': '173001', 
    'Una': '174303', 'Hamirpur': '177001', 'Bilaspur': '174001', 'Chamba': '176310', 
    'Kullu': '175101', 'Kangra': '176001', 'Parwanoo': '173220', 'Sundarnagar': '175002', 
    'Rampur': '172001'
  },
  'Jharkhand': {
    'Ranchi': '834001', 'Jamshedpur': '831001', 'Dhanbad': '826001', 'Bokaro': '827001', 
    'Hazaribagh': '825301', 'Deoghar': '814112', 'Phusro': '829144', 'Adityapur': '831013', 
    'Giridih': '815301', 'Ramgarh': '829122', 'Medininagar': '822101', 'Chirkunda': '828202', 
    'Chaibasa': '833201', 'Dumka': '814101', 'Gumla': '835207', 'Lohardaga': '835302', 
    'Pakur': '816107', 'Jamtara': '815351'
  },
  'Karnataka': {
    'Bengaluru': '560001', 'Mysuru': '570001', 'Hubballi': '580020', 'Mangaluru': '575001', 
    'Belagavi': '590001', 'Davangere': '577001', 'Ballari': '583101', 'Vijayapura': '586101', 
    'Shivamogga': '577201', 'Tumakuru': '572101', 'Udupi': '576101', 'Bidar': '585401', 
    'Hassan': '573201', 'Chitradurga': '577501', 'Raichur': '584101', 'Dharwad': '580001', 
    'Bagalkot': '587101', 'Kalaburagi': '585101', 'Mandya': '571401', 'Chikkamagaluru': '577101', 
    'Kolar': '563101', 'Ramanagara': '562159', 'Gadag': '582101', 'Koppal': '583231', 
    'Chamarajanagar': '571313', 'Haveri': '581110', 'Yadgir': '585201', 'Chikkaballapur': '562101', 
    'Kodagu': '571201', 'Bangalore North': '560003', 'Bangalore South': '560041', 
    'Anekal': '562106', 'Whitefield': '560066', 'Electronic City': '560100', 'Hebbal': '560024', 
    'Yeshwanthpur': '560022', 'Rajajinagar': '560010'
  },
  'Kerala': {
    'Kochi': '682001', 'Thiruvananthapuram': '695001', 'Kozhikode': '673001', 'Thrissur': '680001', 
    'Kannur': '670001', 'Kollam': '691001', 'Palakkad': '678001', 'Alappuzha': '688001', 
    'Kottayam': '686001', 'Malappuram': '676505', 'Pathanamthitta': '689645', 'Idukki': '685602', 
    'Wayanad': '670645', 'Kasaragod': '671121', 'Ernakulam': '682011', 'Aluva': '683101', 
    'Perinthalmanna': '679322', 'Chalakudy': '680307', 'Punalur': '691305', 'Attingal': '695101', 
    'Vatakara': '673101', 'Manjeri': '676121', 'Tirur': '676101', 'Guruvayur': '680101', 
    'Irinjalakuda': '680121'
  },
  'Madhya Pradesh': {
    'Bhopal': '462001', 'Indore': '452001', 'Jabalpur': '482001', 'Gwalior': '474001', 
    'Ujjain': '456001', 'Sagar': '470001', 'Dewas': '455001', 'Satna': '485001', 
    'Ratlam': '457001', 'Rewa': '486001', 'Murwara': '483501', 'Singrauli': '486889', 
    'Burhanpur': '450331', 'Khandwa': '450001', 'Bhind': '477001', 'Chhindwara': '480001', 
    'Guna': '473001', 'Shivpuri': '473551', 'Vidisha': '464001', 'Chhatarpur': '471001', 
    'Damoh': '470661', 'Mandsaur': '458001', 'Khargone': '451001', 'Neemuch': '458441', 
    'Pithampur': '454775', 'Hoshangabad': '461001', 'Itarsi': '461111', 'Sehore': '466001', 
    'Betul': '460001', 'Seoni': '480661', 'Morena': '476001', 'Katni': '483501'
  },
  'Maharashtra': {
    'Mumbai': '400001', 'Pune': '411001', 'Nagpur': '440001', 'Thane': '400601', 
    'Nashik': '422001', 'Aurangabad': '431001', 'Solapur': '413001', 'Kolhapur': '416001', 
    'Navi Mumbai': '400703', 'Amravati': '444601', 'Akola': '444001', 'Latur': '413512', 
    'Chandrapur': '442401', 'Jalgaon': '425001', 'Bhiwandi': '421302', 'Sangli': '416416', 
    'Malegaon': '423203', 'Jalna': '431203', 'Dhule': '424001', 'Ahmednagar': '414001', 
    'Ichalkaranji': '416115', 'Parbhani': '431401', 'Panvel': '410206', 'Nanded': '431601', 
    'Kalyan': '421301', 'Vasai-Virar': '401201', 'Mira-Bhayandar': '401107', 'Ulhasnagar': '421001', 
    'Bhusawal': '425201', 'Gondia': '441601', 'Satara': '415001', 'Yavatmal': '445001', 
    'Ratnagiri': '415612', 'Wardha': '442001', 'Washim': '444505', 'Hingoli': '431513', 
    'Beed': '431122', 'Osmanabad': '413501', 'Sindhudurg': '416812', 'Raigad': '402107'
  },
  'Manipur': {
    'Imphal': '795001', 'Thoubal': '795138', 'Bishnupur': '795126', 'Churachandpur': '795128', 
    'Kakching': '795103', 'Moreh': '795131', 'Senapati': '795106', 'Ukhrul': '795142', 
    'Tamenglong': '795141'
  },
  'Meghalaya': {
    'Shillong': '793001', 'Tura': '794001', 'Jowai': '793150', 'Nongstoin': '793119', 
    'Baghmara': '794102', 'Resubelpara': '794108', 'Williamnagar': '794111', 
    'Nongpoh': '793102', 'Mawkyrwat': '793114'
  },
  'Mizoram': {
    'Aizawl': '796001', 'Lunglei': '796701', 'Champhai': '796321', 'Kolasib': '796081', 
    'Serchhip': '796181', 'Saiha': '796901', 'Mamit': '796431', 'Lawngtlai': '796891'
  },
  'Nagaland': {
    'Kohima': '797001', 'Dimapur': '797112', 'Mokokchung': '798601', 'Tuensang': '798612', 
    'Wokha': '797111', 'Zunheboto': '798620', 'Mon': '798621', 'Phek': '797108', 
    'Kiphire': '798611', 'Longleng': '798625'
  },
  'Odisha': {
    'Bhubaneswar': '751001', 'Cuttack': '753001', 'Rourkela': '769001', 'Berhampur': '760001', 
    'Sambalpur': '768001', 'Puri': '752001', 'Balasore': '756001', 'Baripada': '757001', 
    'Bhadrak': '756100', 'Jharsuguda': '768201', 'Angul': '759122', 'Dhenkanal': '759001', 
    'Koraput': '764020', 'Phulbani': '762001', 'Rayagada': '765001', 'Sundargarh': '770001', 
    'Kendujhar': '758001', 'Jajpur': '755001', 'Paradip': '754142', 'Kendrapara': '754211', 
    'Jagatsinghpur': '754103', 'Nabarangpur': '764059', 'Malkangiri': '764045', 
    'Bolangir': '767001', 'Bargarh': '768028'
  },
  'Punjab': {
    'Ludhiana': '141001', 'Amritsar': '143001', 'Jalandhar': '144001', 'Patiala': '147001', 
    'Bathinda': '151001', 'Mohali': '160055', 'Hoshiarpur': '146001', 'Gurdaspur': '143521', 
    'Pathankot': '145001', 'Moga': '142001', 'Barnala': '148101', 'Faridkot': '151203', 
    'Firozpur': '152002', 'Kapurthala': '144601', 'Sangrur': '148001', 'Rupnagar': '140001', 
    'Muktsar': '152026', 'Fazilka': '152123', 'Malerkotla': '148023', 'Tarn Taran': '143401', 
    'Zirakpur': '140603', 'Kharar': '140301'
  },
  'Rajasthan': {
    'Jaipur': '302001', 'Jodhpur': '342001', 'Kota': '324001', 'Bikaner': '334001', 
    'Ajmer': '305001', 'Udaipur': '313001', 'Alwar': '301001', 'Bhilwara': '311001', 
    'Sikar': '332001', 'Bharatpur': '321001', 'Sri Ganganagar': '335001', 'Pali': '306401', 
    'Barmer': '344001', 'Tonk': '304001', 'Jhunjhunu': '333001', 'Chittorgarh': '312001', 
    'Nagaur': '341001', 'Hanumangarh': '335513', 'Dungarpur': '314001', 'Dausa': '303303', 
    'Jalore': '343001', 'Baran': '325205', 'Banswara': '327001', 'Karauli': '322241', 
    'Sawai Madhopur': '322001', 'Dholpur': '328001', 'Bundi': '323001', 'Jaisalmer': '345001', 
    'Sirohi': '307001', 'Rajsamand': '313324', 'Churu': '331001', 'Pratapgarh': '312605', 
    'Jhalawar': '326001'
  },
  'Sikkim': {
    'Gangtok': '737101', 'Namchi': '737126', 'Gyalshing': '737111', 'Mangan': '737116', 
    'Ravangla': '737139', 'Jorethang': '737121'
  },
  'Tamil Nadu': {
    'Chennai': '600001', 'Coimbatore': '641001', 'Madurai': '625001', 'Salem': '636001', 
    'Tiruchirappalli': '620001', 'Tirunelveli': '627001', 'Vellore': '632001', 'Erode': '638001', 
    'Tiruppur': '641601', 'Thoothukudi': '628001', 'Dindigul': '624001', 'Thanjavur': '613001', 
    'Ranipet': '632401', 'Kanchipuram': '631501', 'Nagapattinam': '611001', 'Namakkal': '637001', 
    'Villupuram': '605602', 'Cuddalore': '607001', 'Karur': '639001', 'Nagercoil': '629001', 
    'Ooty': '643001', 'Kumbakonam': '612001', 'Virudhunagar': '626001', 'Sivakasi': '626123', 
    'Hosur': '635109', 'Krishnagiri': '635001', 'Dharmapuri': '636701', 'Ramanathapuram': '623501', 
    'Pudukkottai': '622001', 'Sivaganga': '630561', 'Ariyalur': '621704', 'Perambalur': '621212', 
    'Theni': '625531', 'Tiruvannamalai': '606601', 'Kallakurichi': '606202', 
    'Chengalpattu': '603001', 'Mayiladuthurai': '609001'
  },
  'Telangana': {
    'Hyderabad': '500001', 'Warangal': '506001', 'Nizamabad': '503001', 'Karimnagar': '505001', 
    'Khammam': '507001', 'Ramagundam': '505208', 'Secunderabad': '500003', 'Mancherial': '504208', 
    'Nalgonda': '508001', 'Adilabad': '504001', 'Suryapet': '508213', 'Miryalaguda': '508207', 
    'Jagtial': '505327', 'Siddipet': '502103', 'Sangareddy': '502001', 'Mahabubnagar': '509001', 
    'Wanaparthy': '509103', 'Nagarkurnool': '509209', 'Nirmal': '504106', 'Kamareddy': '503111', 
    'Medak': '502110', 'Narayanpet': '509210', 'Vikarabad': '501101', 'Jangaon': '506167', 
    'Peddapalli': '505172', 'Bhadradri Kothagudem': '507101', 'Mulugu': '506352', 
    'Jayashankar': '506349', 'Kukatpally': '500072', 'Lb Nagar': '500074', 'Uppal': '500039', 
    'Medchal': '501401', 'Shamshabad': '501218'
  },
  'Tripura': {
    'Agartala': '799001', 'Udaipur': '799120', 'Dharmanagar': '799250', 'Sabroom': '799145', 
    'Belonia': '799155', 'Kailasahar': '799277', 'Khowai': '799201', 'Ambassa': '799289'
  },
  'Uttar Pradesh': {
    'Lucknow': '226001', 'Kanpur': '208001', 'Agra': '282001', 'Varanasi': '221001', 
    'Meerut': '250001', 'Prayagraj': '211001', 'Bareilly': '243001', 'Aligarh': '202001', 
    'Moradabad': '244001', 'Gorakhpur': '273001', 'Noida': '201301', 'Ghaziabad': '201001', 
    'Firozabad': '283203', 'Mathura': '281001', 'Muzaffarnagar': '251001', 'Saharanpur': '247001', 
    'Rampur': '244901', 'Shahjahanpur': '242001', 'Farrukhabad': '209625', 'Hapur': '245101', 
    'Etawah': '206001', 'Mirzapur': '231001', 'Bulandshahr': '203001', 'Sambhal': '244302', 
    'Amroha': '244221', 'Hardoi': '241001', 'Azamgarh': '276001', 'Sitapur': '261001', 
    'Lakhimpur': '262701', 'Hathras': '204101', 'Unnao': '209801', 'Jhansi': '284001', 
    'Orai': '285001', 'Banda': '210001', 'Jaunpur': '222001', 'Ballia': '277001', 
    'Deoria': '274001', 'Basti': '272001', 'Faizabad': '224001', 'Sultanpur': '228001', 
    'Raebareli': '229001', 'Fatehpur': '212601', 'Mainpuri': '205001', 'Etah': '207001', 
    'Budaun': '243601', 'Greater Noida': '201308', 'Vrindavan': '281121'
  },
  'Uttarakhand': {
    'Dehradun': '248001', 'Haridwar': '249401', 'Rishikesh': '249201', 'Roorkee': '247667', 
    'Haldwani': '263139', 'Nainital': '263001', 'Rudrapur': '263153', 'Kashipur': '244713', 
    'Kotdwar': '246149', 'Ramnagar': '244715', 'Almora': '263601', 'Pithoragarh': '262501', 
    'Bageshwar': '263642', 'Pauri': '246001', 'Chamoli': '246424', 'Uttarkashi': '249193', 
    'Mussoorie': '248179'
  },
  'West Bengal': {
    'Kolkata': '700001', 'Howrah': '711101', 'Durgapur': '713201', 'Asansol': '713301', 
    'Siliguri': '734001', 'Bardhaman': '713101', 'Malda': '732101', 'Baharampur': '742101', 
    'Habra': '743263', 'Kharagpur': '721301', 'Shantipur': '741404', 'Dankuni': '712311', 
    'Dhulian': '742202', 'Ranaghat': '741201', 'Haldia': '721602', 'Raiganj': '733134', 
    'Krishnanagar': '741101', 'Nabadwip': '741302', 'Medinipur': '721101', 'Jalpaiguri': '735101', 
    'Balurghat': '733101', 'Bankura': '722101', 'Purulia': '723101', 'Cooch Behar': '736101', 
    'Alipurduar': '736121', 'Kalimpong': '734301', 'Darjeeling': '734101', 'Bishnupur': '722122', 
    'Tamluk': '721636', 'Salt Lake': '700064', 'New Town': '700156', 'Barasat': '700124', 
    'Baranagar': '700036', 'Dum Dum': '700028'
  },
  'Delhi': {
    'New Delhi': '110001', 'Central Delhi': '110001', 'North Delhi': '110007', 
    'South Delhi': '110017', 'East Delhi': '110092', 'West Delhi': '110018', 
    'North East Delhi': '110094', 'North West Delhi': '110052', 'South East Delhi': '110019', 
    'South West Delhi': '110021', 'Shahdara': '110032', 'Dwarka': '110075', 'Rohini': '110085', 
    'Janakpuri': '110058', 'Lajpat Nagar': '110024', 'Karol Bagh': '110005', 
    'Connaught Place': '110001', 'Saket': '110017', 'Nehru Place': '110019', 
    'Preet Vihar': '110092', 'Mayur Vihar': '110091', 'Pitampura': '110034', 
    'Vikaspuri': '110018', 'Uttam Nagar': '110059'
  },
  'Chandigarh': {
    'Chandigarh': '160017', 'Manimajra': '160101', 'Panchkula': '134109', 'Mohali': '160055'
  },
  'Jammu & Kashmir': {
    'Srinagar': '190001', 'Jammu': '180001', 'Anantnag': '192101', 'Baramulla': '193101', 
    'Sopore': '193201', 'Kathua': '184101', 'Udhampur': '182101', 'Rajouri': '185131', 
    'Poonch': '185101', 'Pulwama': '192301', 'Budgam': '191111', 'Kulgam': '192315', 
    'Kupwara': '193222', 'Ganderbal': '191201', 'Bandipora': '193502', 'Ramban': '182148', 
    'Reasi': '182311', 'Kishtwar': '182204'
  },
  'Ladakh': {
    'Leh': '194101', 'Kargil': '194103', 'Diskit': '194401', 'Padum': '194302'
  },
  'Puducherry': {
    'Puducherry': '605001', 'Karaikal': '609602', 'Mahe': '673310', 'Yanam': '533464', 
    'Ozhikaraikal': '609602'
  },
  'Andaman & Nicobar': {
    'Port Blair': '744101', 'Diglipur': '744202', 'Car Nicobar': '744301', 'Havelock Island': '744211'
  },
  'Lakshadweep': {
    'Kavaratti': '682555', 'Agatti': '682553', 'Amini': '682552', 'Androth': '682551', 'Minicoy': '682559'
  },
  'Dadra & Nagar Haveli and Daman & Diu': {
    'Silvassa': '396230', 'Daman': '396210', 'Diu': '362520', 'Dadra': '396230'
  }
};

const getCityPincode = (stateName, city) => {
  if (!city) return '';
  const cleanState = (stateName || '').trim();
  const cleanCity = city.trim();
  
  if (cleanState && STATE_CITY_PINCODES[cleanState]) {
    const cityMap = STATE_CITY_PINCODES[cleanState];
    const match = Object.keys(cityMap).find(k => k.toLowerCase() === cleanCity.toLowerCase());
    if (match) return cityMap[match];
  }
  
  for (const s of Object.keys(STATE_CITY_PINCODES)) {
    const cityMap = STATE_CITY_PINCODES[s];
    const match = Object.keys(cityMap).find(k => k.toLowerCase() === cleanCity.toLowerCase());
    if (match) return cityMap[match];
  }
  
  const zonePrefixes = {
    'Delhi': '110001', 'Haryana': '122001', 'Punjab': '141001', 'Himachal Pradesh': '171001', 
    'Jammu & Kashmir': '190001', 'Ladakh': '194101', 'Chandigarh': '160017',
    'Uttar Pradesh': '226001', 'Uttarakhand': '248001',
    'Rajasthan': '302001', 'Gujarat': '380001', 'Dadra & Nagar Haveli and Daman & Diu': '396210',
    'Maharashtra': '400001', 'Goa': '403001', 'Chhattisgarh': '492001', 'Madhya Pradesh': '462001',
    'Andhra Pradesh': '530001', 'Telangana': '500001', 'Karnataka': '560001',
    'Tamil Nadu': '600001', 'Kerala': '682001', 'Puducherry': '605001', 'Lakshadweep': '682555',
    'West Bengal': '700001', 'Odisha': '751001', 'Sikkim': '737101', 'Andaman & Nicobar': '744101',
    'Bihar': '800001', 'Jharkhand': '834001',
    'Assam': '781001', 'Arunachal Pradesh': '791111', 'Manipur': '795001', 'Meghalaya': '793001', 
    'Mizoram': '796001', 'Nagaland': '797001', 'Tripura': '799001'
  };
  
  return zonePrefixes[cleanState] || '560001';
};

const getStateForCity = (city) => {
  if (!city) return '';
  const cleanCity = city.trim().toLowerCase();
  
  for (const stateName of Object.keys(STATE_CITY_PINCODES)) {
    const cityMap = STATE_CITY_PINCODES[stateName];
    const match = Object.keys(cityMap).find(k => k.toLowerCase() === cleanCity);
    if (match) return stateName;
  }
  return '';
};

const SPORT_ICONS = {
  football:   '⚽',
  cricket:    '🏏',
  badminton:  '🏸',
  tennis:     '🎾',
  basketball: '🏀',
  volleyball: '🏐',
};

function sportIcon(sport) {
  return SPORT_ICONS[(sport || '').toLowerCase()] || '🏟️';
}

/* ─────────────────────────────────────────────
   EDIT VENUE MODAL
───────────────────────────────────────────── */
function EditVenueModal({ turf, token, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('basic');
  const existingSlotsConfig = (turf.pricingRules || []).find(r => r.type === 'slots_config' || r.id === 'turf_slots_config');

  const defaultTimings = [
    { id: 1, name: 'Monday - Friday (Weekdays)', open: '06:00 AM', close: '10:00 PM', price: turf.price_per_hour || '', slots: generateSlotsFromRange('06:00 AM', '10:00 PM', parseFloat(turf.price_per_hour) || 0) },
    { id: 2, name: 'Saturday - Sunday (Weekends)', open: '06:00 AM', close: '10:00 PM', price: turf.price_per_hour || '', slots: generateSlotsFromRange('06:00 AM', '10:00 PM', parseFloat(turf.price_per_hour) || 0) }
  ];

  const initialRawTimings = turf.timings || existingSlotsConfig?.timings || defaultTimings;
  const initialTimings = initialRawTimings.map((t, idx) => ({
    ...t,
    id: t.id || (idx + 1),
    slots: Array.isArray(t.slots) && t.slots.length > 0
      ? t.slots
      : generateSlotsFromRange(t.open || '06:00 AM', t.close || '10:00 PM', parseFloat(t.price || turf.price_per_hour) || 0)
  }));

  const [form, setForm] = useState({
    name: turf.name || '',
    description: turf.description || '',
    shortDescription: turf.short_description || '',
    location: turf.location || '',
    city: turf.city || '',
    state: turf.state || '',
    pincode: turf.pincode || '',
    sport: turf.sport || 'Football',
    sports: turf.sports || (turf.sport ? [turf.sport] : ['Football']),
    type: turf.type || 'Outdoor',
    venueSize: turf.venue_size || 'Medium (8,000 - 12,000 sq.ft)',
    surfaceType: turf.surface_type || 'Artificial Turf',
    bookingType: turf.booking_type || 'Hourly',
    price_per_hour: turf.price_per_hour || '',
    amenities: turf.amenities || [],
    images: turf.images || [],
    videos: turf.videos || [],
    timings: initialTimings
  });

  const defaultSlotsList = [
    { label: "06:00 AM - 07:00 AM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "07:00 AM - 08:00 AM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "08:00 AM - 09:00 AM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "09:00 AM - 10:00 AM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "10:00 AM - 11:00 AM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "11:00 AM - 12:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "12:00 PM - 01:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "01:00 PM - 02:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "02:00 PM - 03:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "03:00 PM - 04:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "04:00 PM - 05:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "05:00 PM - 06:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "06:00 PM - 07:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "07:00 PM - 08:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "08:00 PM - 09:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 },
    { label: "09:00 PM - 10:00 PM", isActive: true, price: parseFloat(turf.price_per_hour) || 0 }
  ];

  const normalizeInitialSlots = (list) => {
    if (!list || list.length === 0) return defaultSlotsList;
    return list.map(s => {
      if (typeof s === 'string') return { label: s, isActive: true, price: parseFloat(turf.price_per_hour) || 0 };
      return {
        label: s.label || s.slot || '',
        isActive: s.isActive !== false,
        price: s.price !== undefined ? parseFloat(s.price) : (parseFloat(turf.price_per_hour) || 0)
      };
    });
  };

  const [feeSettings, setFeeSettings] = useState({
    turf_partner_fee_type: 'percentage',
    turf_partner_fee_value: '5',
  });

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/owner/fee-settings`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (res.data) setFeeSettings(res.data);
    })
    .catch(err => console.error('Failed to load fee settings in EditVenueModal:', err));
  }, [token]);

  const [weekdaySlots, setWeekdaySlots] = useState(normalizeInitialSlots(existingSlotsConfig?.weekdaySlots));
  const [weekendSlots, setWeekendSlots] = useState(normalizeInitialSlots(existingSlotsConfig?.weekendSlots));
  const [showAddSlotForm, setShowAddSlotForm] = useState(false);
  const [newSlotStart, setNewSlotStart] = useState('06:00 AM');
  const [newSlotEnd, setNewSlotEnd] = useState('06:00 PM');
  const [newSlotPrice, setNewSlotPrice] = useState('');
  const [newSlotTarget, setNewSlotTarget] = useState('both');
  const [newSlotMode, setNewSlotMode] = useState('generate_hourly'); // 'generate_hourly' | 'apply_range_price' | 'single_custom'
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [editingSlotPriceKey, setEditingSlotPriceKey] = useState(null);
  const [inlinePriceVal, setInlinePriceVal] = useState('');

  const normalizeSlot = (s) => {
    if (typeof s === 'string') return { label: s, isActive: true, price: parseFloat(form.price_per_hour) || 0 };
    return {
      label: s.label || s.slot || '',
      isActive: s.isActive !== false,
      price: s.price !== undefined ? parseFloat(s.price) : (parseFloat(form.price_per_hour) || 0)
    };
  };

  const toggleSlotActiveState = (targetSlot, isWeekday) => {
    const normTarget = normalizeSlot(targetSlot);
    const setter = isWeekday ? setWeekdaySlots : setWeekendSlots;
    setter(prev => prev.map(s => {
      const norm = normalizeSlot(s);
      if (norm.label === normTarget.label) {
        return { ...norm, isActive: !norm.isActive };
      }
      return norm;
    }));
  };

  const removeSlotPermanently = (targetSlot, isWeekday) => {
    const normTarget = normalizeSlot(targetSlot);
    const setter = isWeekday ? setWeekdaySlots : setWeekendSlots;
    setter(prev => prev.filter(s => normalizeSlot(s).label !== normTarget.label));
  };

  const updateIndividualSlotPrice = (targetSlot, newPrice, isWeekday) => {
    const normTarget = normalizeSlot(targetSlot);
    const numPrice = parseFloat(newPrice) || 0;
    const setter = isWeekday ? setWeekdaySlots : setWeekendSlots;
    setter(prev => prev.map(s => {
      const norm = normalizeSlot(s);
      if (norm.label === normTarget.label) {
        return { ...norm, price: numPrice };
      }
      return norm;
    }));
    setEditingSlotPriceKey(null);
  };

  const handleScheduleSlotPriceUpdate = (scheduleId, slotLabel, newPrice) => {
    const pNum = parseFloat(newPrice) || 0;
    setForm(prev => ({
      ...prev,
      timings: (prev.timings || []).map(t => {
        if (t.id === scheduleId) {
          const currentSlots = Array.isArray(t.slots) && t.slots.length > 0
            ? t.slots
            : generateSlotsFromRange(t.open || '06:00 AM', t.close || '10:00 PM', parseFloat(t.price || form.price_per_hour) || 0);
          const updatedSlots = currentSlots.map(s => {
            if (s.label === slotLabel) {
              return { ...s, price: pNum };
            }
            return s;
          });
          return { ...t, slots: updatedSlots };
        }
        return t;
      })
    }));
  };

  const handleScheduleSlotToggleActive = (scheduleId, slotLabel) => {
    setForm(prev => ({
      ...prev,
      timings: (prev.timings || []).map(t => {
        if (t.id === scheduleId) {
          const currentSlots = Array.isArray(t.slots) && t.slots.length > 0
            ? t.slots
            : generateSlotsFromRange(t.open || '06:00 AM', t.close || '10:00 PM', parseFloat(t.price || form.price_per_hour) || 0);
          const updatedSlots = currentSlots.map(s => {
            if (s.label === slotLabel) {
              return { ...s, isActive: s.isActive === false ? true : false };
            }
            return s;
          });
          return { ...t, slots: updatedSlots };
        }
        return t;
      })
    }));
  };

  const handleScheduleSlotDelete = (scheduleId, slotLabel) => {
    setForm(prev => ({
      ...prev,
      timings: (prev.timings || []).map(t => {
        if (t.id === scheduleId) {
          const currentSlots = Array.isArray(t.slots) && t.slots.length > 0
            ? t.slots
            : generateSlotsFromRange(t.open || '06:00 AM', t.close || '10:00 PM', parseFloat(t.price || form.price_per_hour) || 0);
          const updatedSlots = currentSlots.filter(s => s.label !== slotLabel);
          return { ...t, slots: updatedSlots };
        }
        return t;
      })
    }));
  };

  const handleResetScheduleSlots = (scheduleId) => {
    setForm(prev => ({
      ...prev,
      timings: (prev.timings || []).map(t => {
        if (t.id === scheduleId) {
          const schedPrice = parseFloat(t.price || form.price_per_hour) || 0;
          return {
            ...t,
            slots: generateSlotsFromRange(t.open || '06:00 AM', t.close || '10:00 PM', schedPrice)
          };
        }
        return t;
      })
    }));
  };

  const checkSlotOverlap = (newSlotStr, existingSlots) => {
    const newRange = parseSlotRangeToMinutes(newSlotStr);
    if (!newRange) return null;

    for (const slotItem of existingSlots) {
      const slotStr = typeof slotItem === 'string' ? slotItem : (slotItem?.label || slotItem?.slot || '');
      const existingRange = parseSlotRangeToMinutes(slotStr);
      if (!existingRange) continue;

      if (newRange.start < existingRange.end && existingRange.start < newRange.end) {
        return slotStr;
      }
    }
    return null;
  };

  const syncTimingScheduleFromConfig = (target, startStr, endStr, price) => {
    let dayRangeName = 'Monday - Friday (Weekdays)';
    if (target === 'weekend' || target === 'sat-sun') {
      dayRangeName = 'Saturday - Sunday (Weekends)';
    } else if (target === 'both' || target === 'all-days') {
      dayRangeName = 'All Days (Monday - Sunday)';
    } else if (target === 'mon-wed') {
      dayRangeName = 'Monday - Wednesday';
    } else if (target === 'thu-fri') {
      dayRangeName = 'Thursday - Friday';
    } else if (target === 'mon-sat') {
      dayRangeName = 'Monday - Saturday';
    } else {
      const matchOpt = DAY_RANGE_OPTIONS.find(d => d.id === target || d.label === target);
      if (matchOpt) dayRangeName = matchOpt.label;
    }

    setForm(prev => {
      const list = [...(prev.timings || [])];
      const matchIdx = list.findIndex(t => t.name === dayRangeName);
      if (matchIdx >= 0) {
        list[matchIdx] = {
          ...list[matchIdx],
          open: startStr,
          close: endStr,
          price: price ? String(price) : list[matchIdx].price
        };
      } else {
        list.push({
          id: list.length + 1,
          name: dayRangeName,
          customName: '',
          open: startStr,
          close: endStr,
          price: price ? String(price) : ''
        });
      }
      return { ...prev, timings: list };
    });
  };

  const handleApplySlotRangeOrBuilder = (forceAdd = false) => {
    const startStr = newSlotStart.trim();
    const endStr = newSlotEnd.trim();
    if (!startStr || !endStr) return;

    const basePriceNum = parseFloat(form.price_per_hour) || 0;
    const priceToSet = newSlotPrice ? parseFloat(newSlotPrice) : basePriceNum;

    // Sync to Operational Days & Schedule
    syncTimingScheduleFromConfig(newSlotTarget, startStr, endStr, priceToSet);

    if (newSlotMode === 'apply_range_price') {
      const rangeMins = parseSlotRangeToMinutes(`${startStr} - ${endStr}`);
      if (!rangeMins) return;

      const updateList = (prev) => prev.map(s => {
        const norm = normalizeSlot(s);
        const slotMins = parseSlotRangeToMinutes(norm.label);
        if (slotMins && slotMins.start >= rangeMins.start && slotMins.end <= rangeMins.end) {
          return { ...norm, price: priceToSet };
        }
        return norm;
      });

      if (newSlotTarget === 'weekday' || newSlotTarget === 'both' || newSlotTarget === 'mon-fri' || newSlotTarget === 'mon-wed') {
        setWeekdaySlots(updateList);
      }
      if (newSlotTarget === 'weekend' || newSlotTarget === 'both' || newSlotTarget === 'sat-sun') {
        setWeekendSlots(updateList);
      }
      setShowAddSlotForm(false);
      setOverlapWarning(null);
      return;
    }

    if (newSlotMode === 'generate_hourly') {
      const generated = generateSlotsFromRange(startStr, endStr, priceToSet, 60);

      const updateWithGenerated = (prev) => {
        const currentNorm = prev.map(normalizeSlot);
        const merged = [...currentNorm];
        generated.forEach(gen => {
          const existingIdx = merged.findIndex(m => m.label === gen.label);
          if (existingIdx >= 0) {
            merged[existingIdx] = { ...merged[existingIdx], price: gen.price, isActive: true };
          } else {
            merged.push(gen);
          }
        });
        return sortAndDeduplicateSlots(merged, basePriceNum);
      };

      if (newSlotTarget === 'weekday' || newSlotTarget === 'both' || newSlotTarget === 'mon-fri' || newSlotTarget === 'mon-wed') {
        setWeekdaySlots(updateWithGenerated);
      }
      if (newSlotTarget === 'weekend' || newSlotTarget === 'both' || newSlotTarget === 'sat-sun') {
        setWeekendSlots(updateWithGenerated);
      }
      setShowAddSlotForm(false);
      setOverlapWarning(null);
      return;
    }

    // Single custom slot mode
    const slotLabel = `${startStr} - ${endStr}`;
    if (!forceAdd) {
      let overlapping = null;
      if (newSlotTarget === 'weekday' || newSlotTarget === 'both') {
        overlapping = checkSlotOverlap(slotLabel, weekdaySlots);
      }
      if (!overlapping && (newSlotTarget === 'weekend' || newSlotTarget === 'both')) {
        overlapping = checkSlotOverlap(slotLabel, weekendSlots);
      }

      if (overlapping) {
        setOverlapWarning({
          slotLabel,
          overlappingSlot: overlapping,
          target: newSlotTarget
        });
        return;
      }
    }

    const slotObj = { label: slotLabel, isActive: true, price: priceToSet };

    if (newSlotTarget === 'weekday' || newSlotTarget === 'both') {
      setWeekdaySlots(prev => {
        const normList = prev.map(normalizeSlot);
        const idx = normList.findIndex(s => s.label === slotLabel);
        if (idx >= 0) {
          normList[idx] = slotObj;
          return sortAndDeduplicateSlots(normList, basePriceNum);
        }
        return sortAndDeduplicateSlots([...normList, slotObj], basePriceNum);
      });
    }
    if (newSlotTarget === 'weekend' || newSlotTarget === 'both') {
      setWeekendSlots(prev => {
        const normList = prev.map(normalizeSlot);
        const idx = normList.findIndex(s => s.label === slotLabel);
        if (idx >= 0) {
          normList[idx] = slotObj;
          return sortAndDeduplicateSlots(normList, basePriceNum);
        }
        return sortAndDeduplicateSlots([...normList, slotObj], basePriceNum);
      });
    }

    setOverlapWarning(null);
    setShowAddSlotForm(false);
  };

  const handleTimingChange = (item, field, val) => {
    const updated = (form.timings || []).map(t => {
      if (t.id === item.id) {
        const next = { ...t, [field]: val };
        if (field === 'name' && val === 'Custom Days Range...' && !next.customName) {
          next.customName = 'Monday - Wednesday';
        }
        // Regenerate or update slots for this specific schedule item when timing or price changes
        if (field === 'open' || field === 'close' || field === 'price') {
          const sPrice = parseFloat(field === 'price' ? val : (next.price || form.price_per_hour)) || 0;
          const sOpen = field === 'open' ? val : next.open;
          const sClose = field === 'close' ? val : next.close;
          if (sOpen && sClose && sOpen !== sClose) {
            next.slots = generateSlotsFromRange(sOpen, sClose, sPrice);
          }
        }
        return next;
      }
      return t;
    });
    setForm({ ...form, timings: updated });
  };

  const handleAddTimingSchedule = () => {
    const nextId = Date.now();
    const newSchedule = {
      id: nextId,
      name: 'Monday - Wednesday',
      customName: '',
      open: '06:00 AM',
      close: '10:00 PM',
      price: '',
      slots: generateSlotsFromRange('06:00 AM', '10:00 PM', parseFloat(form.price_per_hour) || 0)
    };
    setForm(prev => ({ ...prev, timings: [...(prev.timings || []), newSchedule] }));
  };

  const handleRemoveTimingSchedule = (id) => {
    setForm(prev => {
      const filtered = (prev.timings || []).filter(t => t.id !== id);
      if (filtered.length === 0) {
        return {
          ...prev,
          timings: [
            { id: 1, name: 'Monday - Friday (Weekdays)', customName: '', open: '06:00 AM', close: '10:00 PM', price: '', slots: generateSlotsFromRange('06:00 AM', '10:00 PM', parseFloat(form.price_per_hour) || 0) }
          ]
        };
      }
      return { ...prev, timings: filtered };
    });
  };

  const [newImageUrl, setNewImageUrl] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAddCustomSport, setShowAddCustomSport] = useState(false);
  const [customSportName, setCustomSportName] = useState('');

  const SPORTS_LIST = ['Football', 'Cricket', 'Tennis', 'Volleyball', 'Table Tennis', 'Swimming', 'Badminton', 'Basketball'];
  
  const AMENITIES_LIST = [
    { id: 'floodlights', label: 'Floodlights' },
    { id: 'changing', label: 'Changing Rooms' },
    { id: 'parking', label: 'Parking' },
    { id: 'water', label: 'Drinking Water' },
    { id: 'seating', label: 'Seating Area' },
    { id: 'washrooms', label: 'Washrooms' },
    { id: 'firstaid', label: 'First Aid' },
    { id: 'cafeteria', label: 'Cafeteria' },
    { id: 'wifi', label: 'Wi-Fi' },
    { id: 'music', label: 'Music System' },
    { id: 'ac', label: 'AC Pavilion' },
  ];

  const handleSportToggle = (s) => {
    const isSelected = form.sports.includes(s);
    let updatedSports;
    if (isSelected) {
      updatedSports = form.sports.filter(item => item !== s);
    } else {
      updatedSports = [...form.sports, s];
    }
    setForm({
      ...form,
      sports: updatedSports,
      sport: updatedSports.length > 0 ? updatedSports[0] : ''
    });
  };

  const handleAmenityToggle = (aId) => {
    const isSelected = form.amenities.includes(aId);
    setForm({
      ...form,
      amenities: isSelected 
        ? form.amenities.filter(item => item !== aId) 
        : [...form.amenities, aId]
    });
  };

  const addImageUrl = () => {
    const url = newImageUrl.trim();
    if (url) {
      setForm({ ...form, images: [...form.images, url] });
      setNewImageUrl('');
    }
  };

  const removeImageUrl = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const addVideoUrl = () => {
    const url = newVideoUrl.trim();
    if (url) {
      setForm({ ...form, videos: [...form.videos, url] });
      setNewVideoUrl('');
    }
  };

  const removeVideoUrl = (idx) => {
    setForm({ ...form, videos: form.videos.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return setError('Venue name is required');
    if (form.sports.length === 0) return setError('Please select at least one sport');
    if (!form.price_per_hour || isNaN(parseFloat(form.price_per_hour)) || parseFloat(form.price_per_hour) < 0) {
      return setError('Please enter a valid price per hour');
    }
    
    setSaving(true);
    setError('');
    try {
      const wdSchedulePrice = parseFloat((form.timings || []).find(t => t.name?.includes('Weekday') || t.name?.includes('Monday - Friday'))?.price) || 0;
      const weSchedulePrice = parseFloat((form.timings || []).find(t => t.name?.includes('Weekend') || t.name?.includes('Saturday - Sunday'))?.price) || 0;
      const basePriceNum = parseFloat(form.price_per_hour) || wdSchedulePrice || weSchedulePrice || 0;
      const customRangeRules = [];

      // Extract custom slot rules from weekday slots (only if price differs from weekday schedule rate)
      weekdaySlots.forEach(s => {
        const sPrice = parseFloat(s?.price);
        const refPrice = wdSchedulePrice || basePriceNum;
        if (s && sPrice && sPrice !== refPrice) {
          const parsedRange = parseSlotRangeToMinutes(s.label);
          if (parsedRange) {
            customRangeRules.push({
              id: `rule-wd-${s.label.replace(/\s+/g, '')}`,
              category: 'Custom Slot Rate',
              type: 'Weekday',
              startTime: parsedRange.startStr || s.label.split(/ - | to /)[0],
              endTime: parsedRange.endStr || s.label.split(/ - | to /)[1],
              price: sPrice,
              active: s.isActive !== false
            });
          }
        }
      });

      // Extract custom slot rules from weekend slots (only if price differs from weekend schedule rate)
      weekendSlots.forEach(s => {
        const sPrice = parseFloat(s?.price);
        const refPrice = weSchedulePrice || basePriceNum;
        if (s && sPrice && sPrice !== refPrice) {
          const parsedRange = parseSlotRangeToMinutes(s.label);
          if (parsedRange) {
            customRangeRules.push({
              id: `rule-we-${s.label.replace(/\s+/g, '')}`,
              category: 'Weekend Slot Rate',
              type: 'Weekend',
              startTime: parsedRange.startStr || s.label.split(/ - | to /)[0],
              endTime: parsedRange.endStr || s.label.split(/ - | to /)[1],
              price: sPrice,
              active: s.isActive !== false
            });
          }
        }
      });

      // Extract rules from day schedules with custom rates / day ranges
      (form.timings || []).forEach(timing => {
        const schedulePrice = parseFloat(timing.price);
        const timingDayType = timing.name === 'Custom Days Range...' && timing.customName ? timing.customName : timing.name;
        if (schedulePrice && schedulePrice > 0 && schedulePrice !== basePriceNum) {
          customRangeRules.push({
            id: `rule-timing-${timing.id}`,
            category: `${timingDayType} Rate`,
            type: timingDayType,
            startTime: timing.open || '06:00 AM',
            endTime: timing.close || '10:00 PM',
            price: schedulePrice,
            active: true
          });
        }

        // Also check if this timing has overridden individual slots
        const schedSlots = Array.isArray(timing.slots) ? timing.slots : [];
        schedSlots.forEach(s => {
          const sp = parseFloat(s?.price);
          const effectiveSchedPrice = schedulePrice || basePriceNum;
          if (s && sp !== undefined && !isNaN(sp) && sp !== effectiveSchedPrice) {
            const parsedRange = parseSlotRangeToMinutes(s.label);
            customRangeRules.push({
              id: `rule-sched-${timing.id}-${s.label.replace(/\s+/g, '')}`,
              category: `${timingDayType} Custom Slot`,
              type: timingDayType,
              startTime: parsedRange?.startStr || s.label.split(/ - | to /)[0],
              endTime: parsedRange?.endStr || s.label.split(/ - | to /)[1],
              price: sp,
              active: s.isActive !== false
            });
          }
        });
      });

      const otherRules = (turf.pricingRules || []).filter(r => r.type !== 'slots_config' && r.id !== 'turf_slots_config');
      const updatedPricingRules = [
        ...otherRules,
        {
          id: 'turf_slots_config',
          type: 'slots_config',
          weekdaySlots: weekdaySlots,
          weekendSlots: weekendSlots,
          timings: form.timings
        },
        ...customRangeRules
      ];

      await axios.put(`${API}/turfs/${turf._id}`, {
        name: form.name.trim(),
        location: form.location.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        price_per_hour: basePriceNum,
        sport: form.sport,
        sports: form.sports,
        type: form.type,
        venueSize: form.venueSize,
        surfaceType: form.surfaceType,
        bookingType: form.bookingType,
        description: form.description.trim(),
        shortDescription: form.shortDescription.trim(),
        amenities: form.amenities,
        images: form.images,
        videos: form.videos,
        timings: form.timings,
        pricingRules: updatedPricingRules,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '1rem', WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)',
  };
  const modal = {
    background: '#fff', borderRadius: '24px', padding: '2rem',
    width: '100%', maxWidth: '680px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxSizing: 'border-box'
  };
  const tabButton = (id) => ({
    padding: '10px 16px', borderRadius: '12px', border: 'none',
    background: activeTab === id ? '#DCEFB8' : 'transparent',
    color: activeTab === id ? '#084734' : '#374151',
    fontWeight: '850', fontSize: '0.85rem', cursor: 'pointer',
    transition: '0.2s', outline: 'none'
  });
  const inp = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: '1.5px solid #E9EDE8', fontSize: '0.9rem', fontWeight: '600',
    outline: 'none', boxSizing: 'border-box', marginTop: '6px',
  };
  const lbl = { fontSize: '0.75rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const scrollContainer = {
    flex: 1, overflowY: 'auto', paddingRight: '6px', margin: '1rem 0'
  };
  const imageGrid = {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop: '10px'
  };
  const imageItem = {
    position: 'relative', width: '100%', height: '70px', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid #E9EDE8'
  };

  const tabs = [
    { id: 'basic', label: 'Basic Details' },
    { id: 'location', label: 'Location' },
    { id: 'slots', label: 'Pricing & Slots' },
    { id: 'media', label: 'Media & Amenities' },
  ];

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: '900', fontSize: '1.3rem', color: '#0D1F0F', margin: 0 }}>Edit Venue Details</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151' }}>
            <X size={22} />
          </button>
        </div>

        {/* Tab Buttons */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1.5px solid #F1F5F0', paddingBottom: '8px' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabButton(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Scrollable Content */}
        <div style={scrollContainer}>
          {activeTab === 'basic' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={lbl}>Venue Name *</label>
                <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Green Turf Arena" />
              </div>
              <div>
                <label style={lbl}>Sports Offered * (Select all that apply)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                  {Array.from(new Set([...SPORTS_LIST, ...(form.sports || [])])).map(s => {
                    const isSel = form.sports.includes(s);
                    return (
                      <button 
                        key={s} 
                        type="button"
                        onClick={() => handleSportToggle(s)}
                        style={{
                          padding: '6px 12px', borderRadius: '20px', border: '1.5px solid',
                          borderColor: isSel ? '#CEF17B' : '#E9EDE8',
                          background: isSel ? '#DCEFB8' : '#fff',
                          color: isSel ? '#084734' : '#374151',
                          fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', transition: '0.2s'
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}

                  {showAddCustomSport ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="text"
                        placeholder="Sport name"
                        value={customSportName}
                        onChange={(e) => setCustomSportName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = customSportName.trim();
                            if (val) {
                              if (!form.sports.includes(val)) {
                                setForm(prev => ({ ...prev, sports: [...prev.sports, val], sport: prev.sport || val }));
                              }
                              setCustomSportName('');
                              setShowAddCustomSport(false);
                            }
                          }
                        }}
                        autoFocus
                        style={{
                          padding: '5px 10px',
                          borderRadius: '16px',
                          border: '1.5px solid #084734',
                          fontSize: '0.8rem',
                          fontWeight: '700',
                          outline: 'none'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const val = customSportName.trim();
                          if (val) {
                            if (!form.sports.includes(val)) {
                              setForm(prev => ({ ...prev, sports: [...prev.sports, val], sport: prev.sport || val }));
                            }
                            setCustomSportName('');
                            setShowAddCustomSport(false);
                          }
                        }}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '14px',
                          background: '#084734',
                          color: '#CEF17B',
                          border: 'none',
                          fontWeight: '800',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowAddCustomSport(false); setCustomSportName(''); }}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '14px',
                          background: '#F1F5F9',
                          color: '#64748B',
                          border: 'none',
                          fontWeight: '700',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddCustomSport(true)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: '1.5px dashed #084734',
                        background: '#F0FDF4',
                        color: '#084734',
                        fontWeight: '800',
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Plus size={13} /> Add Sport
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label style={lbl}>Venue Type</label>
                <select style={inp} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="Outdoor">Outdoor</option>
                  <option value="Indoor">Indoor</option>
                  <option value="Indoor + Outdoor">Indoor + Outdoor</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={lbl}>Venue Size</label>
                  <input style={inp} value={form.venueSize} onChange={e => setForm({ ...form, venueSize: e.target.value })} placeholder="e.g. 5v5 or 7v7" />
                </div>
                <div>
                  <label style={lbl}>Surface Type</label>
                  <input style={inp} value={form.surfaceType} onChange={e => setForm({ ...form, surfaceType: e.target.value })} placeholder="e.g. Artificial Turf" />
                </div>
              </div>
              <div>
                <label style={lbl}>Short Description</label>
                <input style={inp} value={form.shortDescription} onChange={e => setForm({ ...form, shortDescription: e.target.value })} placeholder="A brief one-liner summary..." />
              </div>
              <div>
                <label style={lbl}>Venue Rules</label>
                <textarea
                  style={{ ...inp, height: '100px', resize: 'vertical' }}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Enter rules for the venue (e.g. non-marking shoes only, no food on turf, arrive 10 mins early)..."
                />
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <label style={lbl}>Location / Landmark *</label>
                <input style={inp} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Koramangala 4th Block" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={lbl}>City *</label>
                  <input style={inp} value={form.city} onChange={e => {
                    const city = e.target.value;
                    const state = getStateForCity(city) || form.state;
                    const pincode = getCityPincode(state, city);
                    setForm({ ...form, city, pincode, state });
                  }} placeholder="e.g. Bengaluru" />
                </div>
                <div>
                  <label style={lbl}>State *</label>
                  <input style={inp} value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} placeholder="e.g. Karnataka" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={lbl}>Pincode *</label>
                  <input style={inp} value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} placeholder="e.g. 560034" />
                </div>
                <div>
                  <label style={lbl}>Latitude</label>
                  <input style={inp} type="number" step="any" value={form.lat || ''} onChange={e => setForm({ ...form, lat: parseFloat(e.target.value) || null })} placeholder="e.g. 12.9716" />
                </div>
                <div>
                  <label style={lbl}>Longitude</label>
                  <input style={inp} type="number" step="any" value={form.lng || ''} onChange={e => setForm({ ...form, lng: parseFloat(e.target.value) || null })} placeholder="e.g. 77.5946" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'slots' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div style={{ background: '#F8FAF7', padding: '1.2rem', borderRadius: '16px', border: '1.5px solid #EEF2E6' }}>
              {/* Operational Days & Schedules */}
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Official Notice Banner for Dynamic Pricing */}
                <div style={{
                  background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 100%)',
                  border: '1.5px solid #86EFAC',
                  borderRadius: '16px',
                  padding: '1rem 1.25rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  boxShadow: '0 2px 10px rgba(22, 101, 52, 0.04)'
                }}>
                  <div style={{
                    background: '#DCFCE7',
                    border: '1px solid #86EFAC',
                    borderRadius: '10px',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '1.1rem'
                  }}>
                    💡
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.86rem', fontWeight: '900', color: '#084734', letterSpacing: '-0.2px' }}>
                        Official Notice: Flexible & Peak-Hour Slot Pricing
                      </span>
                      <span style={{ fontSize: '0.68rem', fontWeight: '800', background: '#084734', color: '#CEF17B', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                        Partner Guide
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#374151', lineHeight: '1.5', fontWeight: '600' }}>
                      You can configure different rates for different slot ranges (e.g. <strong>Morning hours</strong>, <strong>Evening prime-time</strong>, and <strong>Weekends</strong>) by clicking the <strong>"+ Add New Slot Range / Day Schedule"</strong> button below. Each schedule will automatically generate its slots and customers will be charged the exact rate for that specific time window.
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: '1.25rem', background: '#FFFFFF', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #DCEFB8' }}>
                  <label style={{ ...lbl, marginBottom: '6px' }}>Base Rate / Price per Hour (₹) *</label>
                  <input style={inp} type="number" min="1" value={form.price_per_hour} onChange={e => setForm({ ...form, price_per_hour: e.target.value })} placeholder="e.g. 800" />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                      Operational Days & Schedule
                    </label>
                    <span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: '600' }}>Customize opening & closing hours in 12-Hour format</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddTimingSchedule}
                    style={{
                      background: '#F0FDF4', color: '#084734', border: '1.5px solid #DCEFB8',
                      padding: '6px 12px', borderRadius: '10px', fontSize: '0.78rem', fontWeight: '800',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    <Plus size={14} /> Add Day Schedule
                  </button>
                </div>
              </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1.2rem' }}>
                  {(form.timings || []).map(item => (
                    <div key={item.id} style={{ background: '#fff', padding: '12px 16px', borderRadius: '14px', border: '1.5px solid #E9EDE8', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px', flexWrap: 'wrap' }}>
                          <select
                            value={item.name}
                            onChange={(e) => handleTimingChange(item, 'name', e.target.value)}
                            style={{
                              padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #DCEFB8',
                              fontSize: '0.82rem', fontWeight: '800', color: '#084734', background: '#F8FAF7', outline: 'none'
                            }}
                          >
                            {DAY_RANGE_OPTIONS.map(opt => (
                              <option key={opt.id} value={opt.label}>{opt.label}</option>
                            ))}
                          </select>

                          {item.name === 'Custom Days Range...' && (
                            <input
                              type="text"
                              placeholder="e.g. Monday - Wednesday"
                              value={item.customName || ''}
                              onChange={(e) => handleTimingChange(item, 'customName', e.target.value)}
                              style={{
                                padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #DCEFB8',
                                fontSize: '0.82rem', fontWeight: '700', color: '#161616', outline: 'none', minWidth: '160px'
                              }}
                            />
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveTimingSchedule(item.id)}
                          style={{
                            background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C',
                            padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '800',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                          title="Delete day schedule"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', alignItems: 'start' }}>
                        <TimePicker12
                          label="From (12h)"
                          value={item.open}
                          onChange={(val) => handleTimingChange(item, 'open', val)}
                          compact
                        />
                        <TimePicker12
                          label="To (12h)"
                          value={item.close}
                          onChange={(val) => handleTimingChange(item, 'close', val)}
                          compact
                        />
                        <div>
                          <label style={{ fontSize: '0.72rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '4px' }}>Amount (₹/hr)</label>
                          <div style={{ position: 'relative', marginTop: '2px' }}>
                            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontWeight: '800', color: '#084734', fontSize: '0.85rem' }}>₹</span>
                            <input
                              type="number"
                              min="0"
                              placeholder={form.price_per_hour ? `Default: ${form.price_per_hour}` : "Rate"}
                              value={item.price ?? ''}
                              onChange={(e) => handleTimingChange(item, 'price', e.target.value.replace(/[^0-9]/g, ''))}
                              style={{
                                width: '100%',
                                padding: '8px 10px 8px 24px',
                                borderRadius: '8px',
                                border: '1.5px solid #DCEFB8',
                                outline: 'none',
                                fontSize: '0.85rem',
                                fontWeight: '800',
                                color: '#084734',
                                background: '#F8FAF7',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* + Add New Slot Range / Day Schedule Button */}
                  <button
                    type="button"
                    onClick={handleAddTimingSchedule}
                    style={{
                      background: '#F0FDF4',
                      border: '2px dashed #084734',
                      color: '#084734',
                      padding: '10px 16px',
                      borderRadius: '12px',
                      fontWeight: '850',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#DCEFB8'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#F0FDF4'; }}
                  >
                    <Plus size={16} color="#084734" /> Add New Slot Range / Day Schedule
                  </button>
                </div>
              </div>

              {/* DYNAMIC SLOT CARDS FOR EVERY CONFIGURED SCHEDULE RANGE */}
              {(form.timings || []).map((schedule, schedIdx) => {
                const isWeekend = schedule.name?.includes('Weekend') || schedule.name?.includes('Saturday') || schedule.name?.includes('Sunday');
                const isCustom = schedule.name === 'Custom Days Range...' || (!schedule.name?.includes('Weekday') && !schedule.name?.includes('Weekend'));
                const displayName = schedule.name === 'Custom Days Range...' && schedule.customName ? schedule.customName : schedule.name;
                const schedPrice = parseFloat(schedule.price || form.price_per_hour) || 0;
                
                const slotsList = Array.isArray(schedule.slots) && schedule.slots.length > 0
                  ? schedule.slots
                  : generateSlotsFromRange(schedule.open || '06:00 AM', schedule.close || '10:00 PM', schedPrice);

                const cardBorder = isWeekend ? '#FCD34D' : isCustom ? '#86EFAC' : '#DCEFB8';
                const cardBg = isWeekend 
                  ? 'linear-gradient(135deg, #FFFFFF 0%, #FFFDF5 100%)' 
                  : isCustom 
                  ? 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)'
                  : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAF7 100%)';
                const icon = isWeekend ? '🎉' : isCustom ? '⚡' : '📅';
                const badgeColor = isWeekend ? '#92400E' : isCustom ? '#166534' : '#084734';
                const badgeBg = isWeekend ? '#FEF3C7' : isCustom ? '#DCFCE7' : '#DCEFB8';
                const badgeBorder = isWeekend ? '#FDE68A' : isCustom ? '#86EFAC' : '#CEF17B';

                return (
                  <div key={schedule.id || schedIdx} style={{
                    marginTop: '1.25rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: cardBg,
                    padding: '1.25rem 1.5rem',
                    borderRadius: '16px',
                    border: `1.5px solid ${cardBorder}`,
                    boxShadow: '0 6px 20px rgba(8, 71, 52, 0.04)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.15rem' }}>{icon}</span>
                        <div>
                          <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '850', color: badgeColor }}>
                            {displayName}
                          </h5>
                          <div style={{ fontSize: '0.72rem', color: '#64748B', fontWeight: '700', marginTop: '2px' }}>
                            ⏰ {schedule.open || '06:00 AM'} - {schedule.close || '10:00 PM'} • Rate: <span style={{ color: badgeColor, fontWeight: '850' }}>₹{schedPrice}/hr</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleResetScheduleSlots(schedule.id)}
                          style={{ background: '#fff', border: `1.5px solid ${cardBorder}`, color: badgeColor, padding: '3px 10px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer' }}
                          title="Reset slots to schedule rate"
                        >
                          ↺ Reset Clean Slots
                        </button>
                        <span style={{ fontSize: '0.75rem', fontWeight: '850', background: badgeBg, color: badgeColor, padding: '3px 10px', borderRadius: '14px', border: `1px solid ${badgeBorder}` }}>
                          {slotsList.length} Slots
                        </span>
                      </div>
                    </div>

                    {slotsList.length === 0 ? (
                      <div style={{ padding: '1rem', background: '#F8FAF7', borderRadius: '12px', border: '1.5px dashed #CEF17B', textAlign: 'center', color: '#084734', fontSize: '0.82rem', fontWeight: '700' }}>
                        ⚡ Set operational 'From' and 'To' timings in schedule above to generate slots.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', width: '100%' }}>
                        {slotsList.map((slotItem, sIdx) => {
                          const norm = normalizeSlot(slotItem);
                          const isActive = norm.isActive;
                          const slotPrice = norm.price !== undefined ? norm.price : schedPrice;
                          const isEditing = editingSlotPriceKey === `sched-${schedule.id}-${norm.label}`;

                          return (
                            <div key={sIdx} style={{
                              display: 'flex', flexDirection: 'column', gap: '4px',
                              background: isActive ? '#FFFFFF' : '#F1F5F9',
                              border: isActive ? `1.5px solid ${cardBorder}` : '1.5px dashed #94A3B8',
                              color: isActive ? '#084734' : '#64748B',
                              padding: '8px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '800',
                              boxShadow: isActive ? '0 2px 4px rgba(8, 71, 52, 0.03)' : 'none',
                              transition: 'all 0.2s'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>{norm.label}</span>
                                  {!isActive && (
                                    <span style={{ fontSize: '0.75rem' }} title="Freezed">
                                      ❄️
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleScheduleSlotDelete(schedule.id, norm.label)}
                                  style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: '2px' }}
                                  title="Delete slot"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>

                              {/* Price Tag & Quick Inline Edit */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px', borderTop: '1px dashed #E2E8F0', paddingTop: '4px' }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734' }}>₹</span>
                                    <input
                                      type="number"
                                      autoFocus
                                      value={inlinePriceVal}
                                      onChange={e => setInlinePriceVal(e.target.value)}
                                      style={{ width: '55px', padding: '2px 4px', fontSize: '0.78rem', fontWeight: '800', borderRadius: '4px', border: '1.5px solid #084734', outline: 'none' }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleScheduleSlotPriceUpdate(schedule.id, norm.label, inlinePriceVal);
                                        setEditingSlotPriceKey(null);
                                      }}
                                      style={{ background: '#084734', color: '#CEF17B', border: 'none', borderRadius: '4px', padding: '2px 5px', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer' }}
                                    >
                                      ✓
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingSlotPriceKey(null)}
                                      style={{ background: '#E2E8F0', color: '#334155', border: 'none', borderRadius: '4px', padding: '2px 5px', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer' }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    onClick={() => {
                                      setEditingSlotPriceKey(`sched-${schedule.id}-${norm.label}`);
                                      setInlinePriceVal(slotPrice);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', background: '#F0FDF4', padding: '2px 6px', borderRadius: '6px', border: '1.5px solid #DCEFB8' }}
                                    title="Click to edit slot amount"
                                  >
                                    <span style={{ fontSize: '0.75rem', fontWeight: '900', color: '#084734' }}>₹{slotPrice}</span>
                                    <Edit2 size={10} color="#084734" />
                                  </div>
                                )}

                                <button
                                  type="button"
                                  onClick={() => handleScheduleSlotToggleActive(schedule.id, norm.label)}
                                  title={isActive ? "Freeze slot" : "Activate slot"}
                                  style={{
                                    background: isActive ? '#FEF3C7' : '#D1FAE5',
                                    color: isActive ? '#92400E' : '#065F46',
                                    border: 'none', borderRadius: '6px', padding: '3px 6px',
                                    fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}
                                >
                                  {isActive ? '⏸️ Active' : '▶️ Freezed'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}



              {/* PRICING & EARNINGS PREVIEW BREAKDOWN CARD */}
              {(() => {
                const pType = feeSettings?.turf_partner_fee_type || 'percentage';
                const pVal = parseFloat(feeSettings?.turf_partner_fee_value ?? '5') || 0;

                const customOverrides = [];
                (form.timings || []).forEach(timing => {
                  const schedPrice = parseFloat(timing.price || form.price_per_hour) || 0;
                  const slots = Array.isArray(timing.slots) ? timing.slots : [];
                  const timingLabel = timing.name === 'Custom Days Range...' && timing.customName ? timing.customName : timing.name;
                  
                  slots.forEach(s => {
                    const norm = normalizeSlot(s);
                    if (norm.isActive && norm.price !== undefined && norm.price !== null && norm.price !== '' && parseFloat(norm.price) !== schedPrice) {
                      customOverrides.push({
                        ...norm,
                        scheduleId: timing.id,
                        timingLabel
                      });
                    }
                  });
                });

                const totalCustom = customOverrides.length;

                return (
                  <div style={{
                    marginTop: '1.25rem',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1.5px solid #EEF2E6',
                    padding: '1.25rem 1.5rem',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                      <Zap size={16} color="#084734" />
                      <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '850', color: '#084734' }}>
                        Pricing & Earnings Preview Breakdown
                      </h5>
                    </div>

                    {/* Day Schedule & Slot Range Rates Breakdown - Rendered as Distinct Standalone Cards */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                        📅 Schedule & Range Rates ({ (form.timings || []).length })
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                        {(form.timings || []).map((t) => {
                          const isWeekend = t.name?.includes('Weekend') || t.name?.includes('Saturday') || t.name?.includes('Sunday');
                          const isCustom = t.name === 'Custom Days Range...' || (!t.name?.includes('Weekday') && !t.name?.includes('Weekend'));
                          const displayName = t.name === 'Custom Days Range...' && t.customName ? t.customName : t.name;
                          const rangePrice = parseFloat(t.price || form.price_per_hour) || 0;
                          const rangeFee = pType === 'percentage' ? Math.round(rangePrice * (pVal / 100)) : Math.round(pVal);
                          const rangeNet = Math.max(0, Math.round(rangePrice - rangeFee));
                          const itemFeeLabel = pType === 'percentage' ? `Platform Fee (${pVal}%)` : `Platform Fee (₹${pVal} Flat)`;

                          const cardBg = isWeekend ? 'linear-gradient(135deg, #FFFFFF 0%, #FFFDF5 100%)' : isCustom ? 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)' : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAF7 100%)';
                          const cardBorder = isWeekend ? '#FCD34D' : isCustom ? '#86EFAC' : '#DCEFB8';
                          const titleColor = isWeekend ? '#92400E' : isCustom ? '#166534' : '#084734';
                          const badgeBg = isWeekend ? '#FEF3C7' : isCustom ? '#DCFCE7' : '#DCEFB8';
                          const badgeBorder = isWeekend ? '#FDE68A' : isCustom ? '#86EFAC' : '#CEF17B';

                          return (
                            <div key={t.id} style={{
                              background: cardBg,
                              borderRadius: '14px',
                              border: `1.5px solid ${cardBorder}`,
                              padding: '14px',
                              boxShadow: '0 4px 12px rgba(8, 71, 52, 0.03)',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '4px' }}>
                                <span style={{ fontSize: '0.84rem', fontWeight: '850', color: titleColor }}>{displayName}</span>
                                <span style={{ fontSize: '0.72rem', background: badgeBg, color: titleColor, border: `1px solid ${badgeBorder}`, padding: '2px 8px', borderRadius: '6px', fontWeight: '800' }}>
                                  ⏰ {t.open} - {t.close}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px dashed #E2E8F0', fontSize: '0.78rem' }}>
                                  <span style={{ color: '#4B5563', fontWeight: '600' }}>Rate / Hour</span>
                                  <span style={{ fontWeight: '850', color: '#161616', fontSize: '0.88rem' }}>₹ {rangePrice}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px dashed #E2E8F0', fontSize: '0.78rem' }}>
                                  <span style={{ color: '#4B5563', fontWeight: '600' }}>{itemFeeLabel}</span>
                                  <span style={{ fontWeight: '750', color: '#DC2626' }}>- ₹ {rangePrice ? rangeFee : 0}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isWeekend ? '#FEF3C7' : '#DCEFB8', padding: '8px 10px', borderRadius: '8px', border: `1px solid ${isWeekend ? '#FDE68A' : '#CEF17B'}` }}>
                                  <span style={{ fontSize: '0.8rem', fontWeight: '800', color: titleColor }}>You Receive (Net)</span>
                                  <span style={{ fontSize: '0.95rem', fontWeight: '900', color: titleColor }}>₹ {rangePrice ? rangeNet : 0}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Custom Slot Overrides */}
                    <div>
                      <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                        🏷️ Custom Slot Overrides ({totalCustom})
                      </div>
                      {totalCustom === 0 ? (
                        <div style={{ background: '#F0FDF4', border: '1px dashed #BBF7D0', borderRadius: '10px', padding: '10px', textAlign: 'center', fontSize: '0.75rem', fontWeight: '700', color: '#15803D' }}>
                          ✓ All individual slots currently follow schedule rates.
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                          {customOverrides.map((s, idx) => {
                            const sp = parseFloat(s.price) || 0;
                            const sFee = pType === 'percentage' ? Math.round(sp * (pVal / 100)) : Math.round(pVal);
                            const sNet = Math.max(0, Math.round(sp - sFee));
                            const itemFeeLabel = pType === 'percentage' ? `Platform Fee (${pVal}%)` : `Platform Fee (₹${pVal} Flat)`;

                            return (
                              <div key={`edit-c-ov-${idx}`} style={{
                                background: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1.5px solid #86EFAC',
                                padding: '12px',
                                boxShadow: '0 2px 6px rgba(22, 101, 52, 0.03)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                  <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#166534' }}>[{s.timingLabel}]</span>
                                  <span style={{ fontSize: '0.7rem', background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC', padding: '1px 6px', borderRadius: '6px', fontWeight: '800' }}>
                                    {s.label}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px dashed #E2E8F0', fontSize: '0.78rem' }}>
                                    <span style={{ color: '#4B5563', fontWeight: '600' }}>Slot Rate</span>
                                    <span style={{ fontWeight: '850', color: '#161616' }}>₹ {sp}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px', borderBottom: '1px dashed #E2E8F0', fontSize: '0.78rem' }}>
                                    <span style={{ color: '#4B5563', fontWeight: '600' }}>{itemFeeLabel}</span>
                                    <span style={{ fontWeight: '750', color: '#DC2626' }}>- ₹ {sp ? sFee : 0}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#DCFCE7', padding: '6px 8px', borderRadius: '8px', border: '1px solid #86EFAC' }}>
                                    <span style={{ fontSize: '0.78rem', fontWeight: '800', color: '#166534' }}>You Receive (Net)</span>
                                    <span style={{ fontSize: '0.88rem', fontWeight: '900', color: '#166534' }}>₹ {sp ? sNet : 0}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'media' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={lbl}>Amenities Available</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '8px' }}>
                  {AMENITIES_LIST.map(a => {
                    const isSel = form.amenities.includes(a.id);
                    return (
                      <div 
                        key={a.id} 
                        onClick={() => handleAmenityToggle(a.id)}
                        style={{
                          padding: '10px', borderRadius: '12px', border: '1.5px solid',
                          borderColor: isSel ? '#CEF17B' : '#E9EDE8',
                          background: isSel ? '#DCEFB8' : '#fff',
                          color: isSel ? '#084734' : '#374151',
                          fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', transition: '0.2s',
                          textAlign: 'center'
                        }}
                      >
                        {a.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={lbl}>Images Portfolio</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <input style={{ ...inp, marginTop: 0, flex: 1 }} value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} placeholder="Paste Image URL here..." />
                  <button onClick={addImageUrl} style={{ padding: '0 18px', background: '#084734', color: '#CEF17B', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}>Add</button>
                </div>
                {form.images.length > 0 && (
                  <div style={imageGrid}>
                    {form.images.map((url, idx) => {
                      const isVideo = url.toLowerCase().endsWith('.mp4') || url.toLowerCase().includes('/video');
                      return (
                        <div style={imageItem} key={idx}>
                          {isVideo ? (
                            <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} controls />
                          ) : (
                            <img src={url} alt="venue" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                          <button onClick={() => removeImageUrl(idx)} style={{ position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', cursor: 'pointer' }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label style={lbl}>Videos (Optional)</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <input style={{ ...inp, marginTop: 0, flex: 1 }} value={newVideoUrl} onChange={e => setNewVideoUrl(e.target.value)} placeholder="Paste Video URL here..." />
                  <button onClick={addVideoUrl} style={{ padding: '0 18px', background: '#084734', color: '#CEF17B', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}>Add</button>
                </div>
                {form.videos.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {form.videos.map((url, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAF7', padding: '6px 12px', borderRadius: '10px', border: '1.5px solid #E9EDE8' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '400px' }}>{url}</span>
                        <button onClick={() => removeVideoUrl(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: '800', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#FEE2E2', color: '#DC2626', fontWeight: '700', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #E9EDE8', background: '#fff', fontWeight: '700', cursor: 'pointer', color: '#374151' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: GREEN, color: LIME, fontWeight: '800', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PartnerVenues({ data, onAddClick, onTabChange, onRefresh }) {
  const { token } = useAuth();
  const [search, setSearch]       = useState('');
  const [sportFilter, setSport]   = useState('All Sports');
  const [statusFilter, setStatus] = useState('All Status');
  const [activeTab, setActiveTab] = useState('All');

  const turfs = data?.turfs || [];

  /* ── filter helpers ── */
  const filtered = turfs.filter(t => {
    const matchSearch = !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.location || '').toLowerCase().includes(search.toLowerCase());
    const matchSport  = sportFilter === 'All Sports' || (t.sport || '').toLowerCase() === sportFilter.toLowerCase();
    const matchStatus = statusFilter === 'All Status' || (t.isActive !== false ? 'Active' : 'Inactive') === statusFilter;
    const matchTab    = activeTab === 'All' ||
      (activeTab === 'Active'  && t.isActive !== false) ||
      (activeTab === 'Pending' && t.status === 'pending');
    return matchSearch && matchSport && matchStatus && matchTab;
  });

  const activeCount  = turfs.filter(t => t.isActive !== false).length;
  const pendingCount = turfs.filter(t => t.status === 'pending').length;

  /* ── empty state ── */
  if (turfs.length === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: '20px', border: '1.5px solid #E9EDE8', padding: '4rem', textAlign: 'center' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏟️</div>
        <h3 style={{ fontWeight: '800', fontSize: '1.5rem', color: '#0D1F0F', marginBottom: '12px' }}>No Venues Yet</h3>
        <p style={{ color: '#374151', marginBottom: '2rem' }}>Add your first venue to start accepting bookings.</p>
        <button onClick={onAddClick} style={btnPrimary}>
          <Plus size={18} /> Add Your First Venue
        </button>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeIn 0.4s ease-out' }}>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <p style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>
            — MY PORTFOLIO
          </p>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0D1F0F', margin: 0 }}>Your Venues</h2>
          <p style={{ color: '#374151', fontSize: '0.88rem', marginTop: '4px' }}>
            Manage, edit, and track all your listed sports venues
          </p>
        </div>
        <button onClick={onAddClick} style={btnPrimary}>
          <Plus size={18} /> Add New Venue
        </button>
      </div>

      {/* ── FILTERS ROW ── */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#374151' }} />
          <input
            type="text"
            placeholder="Search venues..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px 10px 40px', borderRadius: '12px',
              border: '1.5px solid #E9EDE8', fontSize: '0.88rem', outline: 'none',
              fontWeight: '500', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Sport filter */}
        <select value={sportFilter} onChange={e => setSport(e.target.value)} style={selectStyle}>
          <option>All Sports</option>
          <option>Football</option>
          <option>Cricket</option>
          <option>Badminton</option>
          <option>Tennis</option>
        </select>

        {/* Status filter */}
        <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
          <option>Pending</option>
        </select>

        {/* Tab pills */}
        <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
          {[
            { label: `All (${turfs.length})`,       key: 'All' },
            { label: `Active (${activeCount})`,      key: 'Active' },
            { label: `Pending (${pendingCount})`,    key: 'Pending' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '8px 16px', borderRadius: '10px', border: '1.5px solid',
                borderColor: activeTab === t.key ? GREEN : '#E9EDE8',
                background: activeTab === t.key ? GREEN : '#fff',
                color: activeTab === t.key ? LIME : '#6B7280',
                fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── VENUE CARDS GRID ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.2rem' }}>
        {filtered.map(turf => (
          <VenueCard
            key={turf._id}
            turf={turf}
            bookings={data?.bookings}
            token={token}
            onTabChange={onTabChange}
            onRefresh={onRefresh}
          />
        ))}

        {/* Add new venue card */}
        <div
          onClick={onAddClick}
          style={{
            background: '#fff', borderRadius: '20px',
            border: '2px dashed #D1D5DB', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '3rem 2rem', cursor: 'pointer', minHeight: '320px',
            transition: 'border-color 0.2s',
          }}
        >
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: '#F0FDF4', display: 'flex', alignItems: 'center',
            justifyContent: 'center', marginBottom: '1rem',
          }}>
            <Plus size={28} color={GREEN} />
          </div>
          <h4 style={{ fontWeight: '800', fontSize: '1rem', color: '#0D1F0F', marginBottom: '8px' }}>Add a New Venue</h4>
          <p style={{ color: '#374151', fontSize: '0.82rem', textAlign: 'center', lineHeight: 1.5, marginBottom: '1.5rem' }}>
            List another sports venue and start earning from more bookings
          </p>
          <button style={btnPrimary}>+ Add Venue</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   VENUE CARD  (matches the design mockup)
───────────────────────────────────────────── */
function VenueCard({ turf, bookings, token, onTabChange, onRefresh }) {
  const { token: authToken } = useAuth();
  const tk = token || authToken;

  const [isActive, setIsActive]   = useState(turf.isActive !== false);
  const [editOpen, setEditOpen]   = useState(false);

  const turfBookings = bookings?.filter(b =>
    (b.turf_id?._id || b.turf_id) === turf._id
  ) || [];

  const totalEarned = turf.earnings || 0;

  const rating    = turf.rating || 4.5;
  const reviews   = turf.reviewCount || 0;
  const amenities = (turf.amenities || []).slice(0, 3);

  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await axios.patch(`${API}/turfs/${turf._id}/status`, {}, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      const newActive = res.data.isActive;
      setIsActive(newActive);
      alert(newActive ? 'Venue activated successfully!' : 'Venue deactivated successfully!');
      onRefresh?.();
    } catch (err) {
      console.error('Failed to toggle venue status:', err);
      alert(err.response?.data?.msg || 'Failed to update venue status.');
    } finally {
      setToggling(false);
    }
  };

  const handleDeleteTurf = async (turfId) => {
    if (!window.confirm('Are you sure you want to delete this venue? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/turfs/${turfId}`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      alert('Venue deleted successfully!');
      onRefresh?.(); // Refresh the venues list
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.msg || 'Failed to delete venue. Please try again.');
    }
  };

  return (
    <div style={{
      background: '#fff', borderRadius: '20px',
      border: '1.5px solid #E9EDE8', overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column',
    }}>
      {editOpen && (
        <EditVenueModal
          turf={turf}
          token={tk}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onRefresh?.(); }}
        />
      )}
      {/* Image / sport banner */}
      <div style={{ position: 'relative', height: '160px', background: GREEN, overflow: 'hidden' }}>
        {(() => {
          let imgs = turf.images;
          if (typeof imgs === 'string') {
            try { imgs = JSON.parse(imgs); } catch { imgs = [imgs]; }
          }
          const firstImg = Array.isArray(imgs) && imgs.length > 0 ? imgs[0] : null;
          const rawUrl = typeof firstImg === 'object' && firstImg?.url ? firstImg.url : (typeof firstImg === 'string' ? firstImg : '');
          if (!rawUrl) {
            return (
              <div style={{
                width: '100%', height: '100%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: '4rem',
              }}>
                {sportIcon(turf.sport)}
              </div>
            );
          }
          const safeUrl = (rawUrl.startsWith('http://') && !rawUrl.includes('localhost') && !rawUrl.includes('127.0.0.1'))
            ? rawUrl.replace('http://', 'https://')
            : rawUrl;
          const isVideo = safeUrl.toLowerCase().endsWith('.mp4') || safeUrl.toLowerCase().includes('/video');
          return isVideo ? (
            <video
              src={safeUrl}
              muted
              loop
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
            />
          ) : (
            <img
              src={safeUrl}
              alt={turf.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
            />
          );
        })()}

        {/* Sport badge */}
        <div style={{
          position: 'absolute', top: '12px', left: '12px',
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          fontSize: '0.68rem', fontWeight: '800', letterSpacing: '1px',
          padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase',
        }}>
          {turf.sport || 'Multi-sport'}
        </div>

        {/* Active badge */}
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          background: turf.status === 'pending' ? '#FEF3C7' : isActive ? '#D1FAE5' : '#F3F4F6',
          color: turf.status === 'pending' ? '#92400E' : isActive ? '#065F46' : '#6B7280',
          fontSize: '0.68rem', fontWeight: '800',
          padding: '4px 10px', borderRadius: '6px',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: turf.status === 'pending' ? '#F59E0B' : isActive ? '#10B981' : '#9CA3AF', display: 'inline-block' }} />
          {turf.status === 'pending' ? 'Pending Approval' : isActive ? 'Active' : 'Inactive'}
        </div>

        {/* Rating */}
        {reviews > 0 && (
          <div style={{
            position: 'absolute', bottom: '12px', left: '12px',
            background: 'rgba(0,0,0,0.6)', color: '#fff',
            fontSize: '0.72rem', fontWeight: '700',
            padding: '4px 10px', borderRadius: '6px',
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <Star size={12} fill="#FBBF24" color="#FBBF24" />
            {rating.toFixed(1)} · {reviews} reviews
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '1.2rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ fontWeight: '800', fontSize: '1.1rem', color: '#0D1F0F', marginBottom: '4px' }}>
          {turf.name}
        </h3>
        <p style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#374151', fontSize: '0.82rem', marginBottom: '1rem' }}>
          <MapPin size={13} /> {turf.location}{turf.city ? `, ${turf.city}` : ''}
        </p>

        {/* Stats row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.5rem', marginBottom: '1rem',
          background: '#F9FAFB', borderRadius: '12px', padding: '0.8rem',
        }}>
          <StatCell label="Bookings" value={turfBookings.filter(b => !['cancelled', 'refund_pending', 'refunded'].includes(b.status)).length} />
          <StatCell
            label="Earned"
            value={totalEarned >= 1000 ? `₹${(totalEarned / 1000).toFixed(0)}K` : `₹${totalEarned}`}
            highlight
          />
          <StatCell label="Rate / hr" value={getTurfPriceRange(turf).text} />
        </div>

        {/* Amenity tags */}
        {amenities.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {amenities.map((a, i) => (
              <span key={i} style={{
                background: '#F0FDF4', color: '#065F46',
                fontSize: '0.7rem', fontWeight: '700',
                padding: '3px 10px', borderRadius: '6px', textTransform: 'capitalize',
              }}>
                {a}
              </span>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
          <button
            onClick={() => onTabChange?.('bookings')}
            style={{ ...btnPrimary, flex: 1, justifyContent: 'center', padding: '10px' }}
          >
            Manage
          </button>
          <button
            onClick={() => setEditOpen(true)}
            style={{ ...btnOutline, flex: 1, justifyContent: 'center', padding: '10px' }}
          >
            <Edit2 size={14} /> Edit
          </button>
        </div>

        {/* Status Toggle (Deactivate/Activate) & Delete Row */}
        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
          <button
            onClick={handleToggle}
            disabled={toggling}
            style={{
              flex: 1,
              background: isActive ? '#FEF3C7' : '#D1FAE5',
              color: isActive ? '#92400E' : '#065F46',
              border: isActive ? '1.5px solid #FDE68A' : '1.5px solid #A7F3D0',
              padding: '9px',
              borderRadius: '10px',
              fontWeight: '800',
              fontSize: '0.82rem',
              cursor: toggling ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
            title={isActive ? "Deactivate venue listing" : "Activate venue listing"}
          >
            {toggling ? 'Updating...' : isActive ? '⏸️ Deactivate' : '▶️ Activate'}
          </button>

          <button
            onClick={() => handleDeleteTurf(turf._id)}
            style={{
              flex: 1,
              background: '#FEE2E2',
              color: '#DC2626',
              border: '1.5px solid #FECACA',
              padding: '9px',
              borderRadius: '10px',
              fontWeight: '800',
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            title="Delete venue"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, highlight }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '0.95rem', fontWeight: '800',
        color: highlight ? GREEN : '#0D1F0F',
      }}>{value}</div>
      <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
    </div>
  );
}

/* ── shared styles ── */
const btnPrimary = {
  background: GREEN, color: LIME, border: 'none',
  padding: '10px 20px', borderRadius: '10px',
  fontWeight: '800', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '6px',
  cursor: 'pointer',
};

const btnOutline = {
  background: '#fff', color: '#374151',
  border: '1.5px solid #E9EDE8',
  padding: '10px 20px', borderRadius: '10px',
  fontWeight: '700', fontSize: '0.85rem',
  display: 'flex', alignItems: 'center', gap: '6px',
  cursor: 'pointer',
};

const selectStyle = {
  padding: '10px 14px', borderRadius: '12px',
  border: '1.5px solid #E9EDE8', fontSize: '0.88rem',
  fontWeight: '600', color: '#374151', outline: 'none',
  background: '#fff', cursor: 'pointer',
};
